import { t } from '@lingui/core/macro';
import { CarAxleType, TireQuoteItemRecordCustomer, TireQuoteItemTireRecordCustomer } from '@navio/nsdk';

/** One quantity stepper of a tire option: an axle on a staggered option, all tires otherwise. */
type TireOptionQuantityRow = {
  label: string;
  tire: TireQuoteItemTireRecordCustomer;
  min: number;
  max: number;
  initial: number;
};

export type TireOptionQuantityRows = [TireOptionQuantityRow, ...TireOptionQuantityRow[]];

/** The option's steppers; null for a staggered option without both a front and a rear tire. */
export const getTireOptionQuantityRows = (quoteItem: TireQuoteItemRecordCustomer): TireOptionQuantityRows | null => {
  const initialOf = (tire: TireQuoteItemTireRecordCustomer) => tire.quantitySelected || tire.quantityDefault || 0;

  if (quoteItem.isStaggered) {
    const front = quoteItem.quoteItemTires.find((tire) => tire.axle?.type === CarAxleType.FRONT);
    const rear = quoteItem.quoteItemTires.find((tire) => tire.axle?.type === CarAxleType.REAR);
    if (!front || !rear) {
      return null;
    }
    return [
      {
        label: t`Front tires`,
        tire: front,
        min: 1,
        max: front.quantityMax ?? front.quantityDefault,
        initial: initialOf(front)
      },
      {
        label: t`Rear tires`,
        tire: rear,
        min: 1,
        max: rear.quantityMax ?? rear.quantityDefault,
        initial: initialOf(rear)
      }
    ];
  }

  const [first] = quoteItem.quoteItemTires;
  if (!first) {
    return null;
  }
  // Several tires of one size are split evenly on order, so the set never drops below one each.
  const min = quoteItem.quoteItemTires.length > 1 ? 2 : 1;
  const initial = quoteItem.quoteItemTires.reduce((sum, tire) => sum + initialOf(tire), 0);
  return [{ label: t`Tires`, tire: first, min, max: 4, initial }];
};

/** The quote item with `quantitySelected` set from the steppers, as the next steps and the order read it. */
export const withTireOptionQuantities = (
  quoteItem: TireQuoteItemRecordCustomer,
  quantities: number[]
): TireQuoteItemRecordCustomer => {
  if (quoteItem.isStaggered) {
    return {
      ...quoteItem,
      quoteItemTires: quoteItem.quoteItemTires.map((tire) => ({
        ...tire,
        quantitySelected: (tire.axle?.type === CarAxleType.FRONT ? quantities[0] : quantities[1]) ?? 0
      }))
    };
  }

  const total = quantities[0] ?? 0;
  const tireCount = quoteItem.quoteItemTires.length;
  return {
    ...quoteItem,
    quoteItemTires: quoteItem.quoteItemTires.map((tire, index) => ({
      ...tire,
      quantitySelected: Math.floor(total / tireCount) + (index < total % tireCount ? 1 : 0)
    }))
  };
};
